import { DRONE_CONFIG, DRONE_DISPLAY_NAME, getDroneDefinition, isDroneId, isDroneShoutText,
  type DroneId, type DronePlannerJob, type DroneRenderSnapshot, type ServerDroneShoutResult, type ServerDroneTelemetry } from "@endclock/shared";
import type { NetworkClient } from "../game/network.js";

const stateFields = ["STATUS", "TASK", "TASK TIMER", "BATTERY", "FLIGHT", "HOME DISTANCE", "TARGET", "SCANNER"] as const;
const jobFields = ["MODEL", "MODEL QUANT", "REQUEST / JOB ID", "EVENT", "QUEUED AT", "STARTED AT", "FINISHED AT", "QUEUE WAIT", "WALL TIME", "CPU TIME", "AVERAGE CPU %", "CORE EQUIVALENT", "THREAD LIMIT", "PROMPT TOKENS", "OUTPUT TOKENS", "GENERATION TOK/S", "PROMPT TOK/S", "RESULT"] as const;
type PanelNetwork = Pick<NetworkClient, "state" | "estimatedServerNow" | "subscribeDrone" | "shoutAtDrone">;

export function formatTaskTimer(task: DroneRenderSnapshot["currentTask"], serverNow: number): string {
  return `${duration(Math.max(0, serverNow - task.startedAt))} / ${duration(Math.max(0, task.plannedEndAt - task.startedAt))}`;
}

export function droneJobFields(job: DronePlannerJob): Readonly<Record<(typeof jobFields)[number], string>> {
  return {
    MODEL: job.model, "MODEL QUANT": job.quant, "REQUEST / JOB ID": job.jobId, EVENT: job.event,
    "QUEUED AT": timestamp(job.queuedAt), "STARTED AT": timestamp(job.startedAt), "FINISHED AT": timestamp(job.finishedAt),
    "QUEUE WAIT": `${job.queueMs.toFixed(0)} ms`, "WALL TIME": `${job.wallMs.toFixed(0)} ms`,
    "CPU TIME": measured(job.cpuMs, " ms", 0), "AVERAGE CPU %": measured(job.averageCpuPercent, "%", 1),
    "CORE EQUIVALENT": measured(job.coreEquivalent, " cores", 2), "THREAD LIMIT": String(job.threadLimit),
    "PROMPT TOKENS": measured(job.promptTokens, "", 0), "OUTPUT TOKENS": measured(job.outputTokens, "", 0),
    "GENERATION TOK/S": measured(job.generationTokensPerSecond, "", 1), "PROMPT TOK/S": measured(job.promptTokensPerSecond, "", 1), RESULT: job.result,
  };
}

/** Public per-drone UI: a whitelist of summaries/measurements, never raw planner requests. */
export class DroneDebugPanel {
  selectedId: DroneId | null = null;
  captionMaximumX: number | null = null;
  private readonly root = element("aside", "drone-debug-panel");
  private readonly designation = document.createElement("strong");
  private readonly connection = element("span", "drone-connection");
  private readonly summary = element("p", "drone-decision");
  private readonly speech = element("p", "drone-transmission");
  private readonly jobStatus = element("span", "drone-job-status");
  private readonly input = document.createElement("input");
  private readonly send = document.createElement("button");
  private readonly feedback = element("span", "drone-shout-feedback");
  private readonly fields = new Map<string, HTMLElement>();
  private readonly historyRows = Array.from({ length: 8 }, () => document.createElement("li"));
  private lastUpdateAt = -Infinity;
  private lastTelemetry: ServerDroneTelemetry | null = null;
  private pendingRequest: string | null = null;
  private nextShoutAt = 0;
  private inRange = false;

  constructor(parent: HTMLElement, private readonly network: PanelNetwork) {
    this.root.id = "drone-debug-panel";
    this.root.hidden = true;
    this.root.setAttribute("aria-label", "SENTINEL-01 debug AI telemetry");
    const header = element("header", "drone-panel-header");
    const title = document.createElement("div");
    this.designation.textContent = DRONE_DISPLAY_NAME;
    const debug = document.createElement("small"); debug.textContent = "DEBUG · AI TELEMETRY";
    title.append(this.designation, debug);
    const close = document.createElement("button"); close.type = "button"; close.textContent = "×";
    close.id = "drone-debug-close"; close.setAttribute("aria-label", "Close drone telemetry");
    close.addEventListener("click", () => this.select(null));
    header.append(title, close);
    const body = element("div", "drone-panel-body");
    body.append(this.connection, this.createFields(stateFields, "drone-state-grid"));
    body.append(label("AI DECISION"), this.summary, label("LAST TRANSMISSION"), this.speech);
    const form = element("form", "drone-shout-form");
    form.id = "drone-shout-form";
    const inputLabel = document.createElement("label"); inputLabel.textContent = "SHOUT AT DRONE"; inputLabel.htmlFor = "drone-shout-input";
    this.input.id = "drone-shout-input"; this.input.type = "text"; this.input.maxLength = DRONE_CONFIG.shoutMaximumChars;
    this.input.placeholder = "What are you doing?"; this.input.autocomplete = "off";
    this.input.setAttribute("aria-describedby", "drone-shout-feedback");
    this.send.id = "drone-shout-send"; this.send.type = "submit"; this.send.textContent = "SHOUT";
    this.feedback.id = "drone-shout-feedback"; this.feedback.setAttribute("role", "status");
    form.append(inputLabel, this.input, this.send, this.feedback);
    form.addEventListener("submit", (event) => { event.preventDefault(); this.submitShout(); });
    body.append(form);
    const details = document.createElement("details"); details.className = "drone-job-details";
    const jobTitle = document.createElement("summary"); jobTitle.textContent = "AI JOB TELEMETRY";
    details.append(jobTitle, this.jobStatus, this.createFields(jobFields, "drone-job-grid"));
    const historyTitle = label("RECENT AI JOBS · LAST 8");
    const history = document.createElement("ol"); history.id = "drone-job-history";
    for (const row of this.historyRows) { row.hidden = true; history.append(row); }
    details.append(historyTitle, history);
    body.append(details);
    this.root.append(header, body);
    parent.append(this.root);
  }

  select(droneId: string | null): void {
    if (droneId !== null && !isDroneId(droneId)) return;
    if (droneId !== this.selectedId) this.reset();
    this.selectedId = droneId;
    if (droneId) {
      const name = getDroneDefinition(droneId)!.displayName;
      this.designation.textContent = name;
      this.root.setAttribute("aria-label", `${name} debug AI telemetry`);
    }
    this.root.hidden = droneId === null;
    if (droneId === null) this.captionMaximumX = null;
    this.lastUpdateAt = -Infinity;
    this.network.subscribeDrone(droneId);
  }

  reset(): void {
    this.selectedId = null;
    this.captionMaximumX = null;
    this.root.hidden = true;
    this.lastTelemetry = null;
    this.pendingRequest = null;
    this.nextShoutAt = 0;
    this.inRange = false;
    this.input.value = "";
    this.feedback.textContent = "";
    for (const field of this.fields.values()) field.textContent = "—";
    for (const row of this.historyRows) row.hidden = true;
    this.summary.textContent = "Waiting for server telemetry.";
    this.speech.textContent = "—";
    this.jobStatus.textContent = "No planner job received.";
    this.lastUpdateAt = -Infinity;
  }

  update(state: DroneRenderSnapshot | null, telemetry: ServerDroneTelemetry | null, playerX: number, playerY: number, playerZ: number, localNow = performance.now()): void {
    if (this.root.hidden || localNow - this.lastUpdateAt < 100) return;
    // Late packets from a previously selected identity must never populate or
    // enable controls in the newly selected drone's panel.
    if (state?.id !== this.selectedId) state = null;
    if (telemetry?.droneId !== this.selectedId) telemetry = null;
    this.lastUpdateAt = localNow;
    this.captionMaximumX = this.root.getBoundingClientRect().left - 132;
    const online = this.network.state === "online";
    const now = this.network.estimatedServerNow(localNow);
    this.connection.textContent = !online ? "OFFLINE · awaiting connection" : !state ? "OUT OF RANGE · telemetry paused" : telemetry?.plannerReady ? "ONLINE · LOCAL AI READY" : "DEGRADED · DETERMINISTIC FALLBACK";
    this.connection.dataset.state = !online ? "offline" : !state ? "unavailable" : telemetry?.plannerReady ? "ready" : "fallback";
    if (state) {
      const targetDistance = telemetry?.targetDistanceMeters ?? null;
      this.setField("STATUS", state.behaviorState.replaceAll("_", " "));
      this.setField("TASK", state.currentTask.type);
      this.setField("TASK TIMER", formatTaskTimer(state.currentTask, now));
      this.setField("BATTERY", `${state.battery.toFixed(1)}%`);
      this.setField("FLIGHT", state.flightState);
      this.setField("HOME DISTANCE", `${Math.hypot(state.position.x - state.home.x, state.position.z - state.home.z).toFixed(1)} m`);
      this.setField("TARGET", state.targetPlayerId ? `PLAYER ${state.targetPlayerId.slice(0, 8)}${targetDistance === null ? "" : ` · ${targetDistance.toFixed(1)} m`}` : "NONE");
      this.setField("SCANNER", state.scanner.mode === "idle" ? "IDLE" : `ACTIVE · ${state.scanner.mode.toUpperCase()}`);
    }
    if (telemetry && telemetry !== this.lastTelemetry) {
      this.lastTelemetry = telemetry;
      this.summary.textContent = telemetry.decisionSummary || "Awaiting a planner decision.";
      this.speech.textContent = telemetry.lastSpeech?.text ?? "—";
      const jobs = [...telemetry.jobs].sort((a, b) => b.finishedAt - a.finishedAt).slice(0, 8);
      const latest = jobs[0];
      if (latest) {
        const values = droneJobFields(latest);
        for (const key of jobFields) this.setField(key, values[key]);
        this.jobStatus.textContent = `${latest.result.toUpperCase()} · ${latest.wallMs.toFixed(0)} ms wall · ${measured(latest.cpuMs, " ms CPU", 0)}`;
      } else this.jobStatus.textContent = "Waiting for completed planner job telemetry.";
      for (let index = 0; index < this.historyRows.length; index += 1) {
        const job = jobs[index], row = this.historyRows[index]!;
        row.hidden = !job;
        if (job) row.textContent = `${timestamp(job.finishedAt)} · ${job.event} · ${job.result} · ${job.wallMs.toFixed(0)} ms · ${job.jobId}`;
      }
    }
    this.inRange = Boolean(online && state && Math.hypot(playerX - state.position.x, playerY - state.position.y, playerZ - state.position.z) <= DRONE_CONFIG.shoutRange);
    const coolingDown = now < this.nextShoutAt;
    this.send.disabled = !this.inRange || this.pendingRequest !== null || coolingDown;
    this.input.disabled = !online || !state;
    this.send.title = !this.inRange ? "Move within 20 m of the drone." : coolingDown ? "Wait for the shout cooldown." : "Send a text shout to the server.";
    if (!this.pendingRequest && this.feedback.textContent === "") this.feedback.textContent = "Text only · within 20 m · one shout per 5 seconds.";
  }

  handleShoutResult(message: ServerDroneShoutResult): void {
    if (message.requestId !== this.pendingRequest || message.droneId !== this.selectedId) return;
    this.pendingRequest = null;
    if (message.ok) {
      this.feedback.textContent = "Shout received. The drone will decide how to respond.";
      this.input.value = "";
      this.nextShoutAt = this.network.estimatedServerNow() + DRONE_CONFIG.shoutCooldownMs;
    } else {
      const reasons: Readonly<Record<ServerDroneShoutResult["code"], string>> = {
        OK: "Shout received.", UNKNOWN_PLAYER: "Reconnect before shouting.", INVALID_DRONE: "Drone unavailable.",
        INVALID_REQUEST: "Enter 1–160 characters without control characters.", TOO_FAR: "Move within 20 m of the drone.",
        OCCLUDED: "The drone cannot hear you from here.", RATE_LIMITED: "Wait before shouting again.",
      };
      this.feedback.textContent = reasons[message.code];
    }
    this.lastUpdateAt = -Infinity;
  }

  dispose(): void { this.root.remove(); }

  private submitShout(): void {
    const text = this.input.value.trim();
    if (!isDroneShoutText(text)) { this.feedback.textContent = "Enter 1–160 characters without control characters."; return; }
    if (!this.selectedId || !this.inRange || this.pendingRequest || this.network.estimatedServerNow() < this.nextShoutAt) {
      this.feedback.textContent = "Move within 20 m and wait for the shout cooldown."; return;
    }
    this.pendingRequest = this.network.shoutAtDrone(this.selectedId, text);
    this.feedback.textContent = this.pendingRequest ? "Sending shout…" : "Unable to send while offline.";
    this.send.disabled = this.pendingRequest !== null;
  }

  private createFields(keys: readonly string[], className: string): HTMLElement {
    const list = element("dl", className);
    for (const key of keys) {
      const term = document.createElement("dt"); term.textContent = key;
      const value = document.createElement("dd"); value.textContent = "—"; value.dataset.droneField = key.toLowerCase().replaceAll(/[^a-z0-9]+/g, "-");
      this.fields.set(key, value); list.append(term, value);
    }
    return list;
  }
  private setField(key: string, value: string): void { const field = this.fields.get(key); if (field && field.textContent !== value) field.textContent = value; }
}

function element<K extends keyof HTMLElementTagNameMap>(tag: K, className: string): HTMLElementTagNameMap[K] { const node = document.createElement(tag); node.className = className; return node; }
function label(text: string): HTMLElement { const node = element("strong", "drone-section-label"); node.textContent = text; return node; }
function measured(value: number | null, unit: string, decimals: number): string { return value === null || !Number.isFinite(value) ? "unavailable" : `${value.toFixed(decimals)}${unit}`; }
function timestamp(value: number): string { return new Date(value).toISOString().slice(11, 23) + " UTC"; }
function duration(value: number): string { const tenths = Math.floor(value / 100); return `${String(Math.floor(tenths / 600)).padStart(2, "0")}:${String(Math.floor(tenths % 600 / 10)).padStart(2, "0")}.${tenths % 10}`; }
